import React from 'react';

const Section = ({ title, children }) => (
  <section className="mb-8">
    <h2 className="text-lg font-semibold text-slate-800 mb-3">{title}</h2>
    <div className="text-sm text-slate-600 leading-relaxed space-y-3">{children}</div>
  </section>
);

export default function PrivacyPolicyPage() {
  return (
    <div className="min-h-screen bg-slate-50" data-testid="privacy-policy-page">
      <header className="bg-[#0F172A] text-white">
        <div className="max-w-3xl mx-auto px-6 py-10">
          <h1 className="text-3xl font-semibold">Privacy Policy</h1>
          <p className="mt-2 text-sm text-white/70">
            PNA Title Services LLC — TitleFlow Platform · Last updated: July 25, 2026
          </p>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-10">
        <p className="text-sm text-slate-600 leading-relaxed mb-8">
          This Privacy Policy describes how PNA Title Services LLC (&quot;PNA&quot;, &quot;we&quot;,
          &quot;us&quot;) collects, uses, stores, and protects information within TitleFlow, our
          internal title-services operations platform. TitleFlow is a business tool used by
          authorized PNA employees and contractors to process title search orders, tax
          certifications, municipal lien certificate requests, and related human-resources
          workflows.
        </p>

        <Section title="1. Information We Collect">
          <p><strong>Account &amp; employee information.</strong> Name, work email address, role,
          team assignment, employment details (for HR modules such as attendance, leave, and
          recruitment), and login activity (last login time, failed attempts for account
          protection).</p>
          <p><strong>Order &amp; property data.</strong> Information required to fulfill title and
          tax search orders, including property addresses, parcel numbers, county/jurisdiction
          details, borrower and client names, tax amounts, and related documents. This data is
          provided by our clients or sourced from public records for legitimate business
          purposes.</p>
          <p><strong>Operational records.</strong> Audit logs of actions taken in the platform
          (order updates, approvals, policy acknowledgments), workflow notes, and productivity
          metrics used for internal operations management.</p>
        </Section>

        <Section title="2. How We Use Information">
          <ul className="list-disc pl-5 space-y-1">
            <li>To authenticate users and secure access to the platform (role-based access control).</li>
            <li>To process and track title search, tax certification, and mail-away requests.</li>
            <li>To send transactional emails (order deliveries, mail-away letters, system alerts) via Amazon SES.</li>
            <li>To manage HR functions: attendance, leave balances, recruitment, and policy acknowledgments.</li>
            <li>To maintain audit trails required for quality control and compliance.</li>
          </ul>
          <p>We do <strong>not</strong> sell personal information, and we do not use TitleFlow data
          for advertising or third-party marketing.</p>
        </Section>

        <Section title="3. Data Storage & Security">
          <ul className="list-disc pl-5 space-y-1">
            <li>Data is stored in a managed MongoDB database with access restricted to the application.</li>
            <li>Passwords are stored only as salted bcrypt hashes — never in plain text.</li>
            <li>All traffic is encrypted in transit over HTTPS/TLS.</li>
            <li>Sessions use signed JSON Web Tokens with automatic idle-session logout.</li>
            <li>Account lockout protections guard against brute-force login attempts.</li>
            <li>Encrypted backups are stored in Amazon S3 with restricted access.</li>
            <li>Access is governed by role-based permissions; administrative actions are audit-logged.</li>
          </ul>
        </Section>

        <Section title="4. Third-Party Service Providers">
          <p>TitleFlow relies on a small set of infrastructure providers, each processing data
          solely on our behalf:</p>
          <ul className="list-disc pl-5 space-y-1">
            <li><strong>Amazon Web Services (SES)</strong> — delivery of transactional emails.</li>
            <li><strong>Amazon Web Services (S3)</strong> — encrypted database backups and document storage.</li>
          </ul>
          <p>No analytics, advertising, or social-media tracking services are integrated into the
          platform.</p>
        </Section>

        <Section title="5. Cookies & Local Storage">
          <p>TitleFlow uses only <strong>strictly necessary</strong> cookies and browser local
          storage — for login sessions, security, and remembering interface preferences. No
          tracking or advertising cookies are used. See our{' '}
          <a href="/cookie-policy" className="text-blue-600 hover:underline">Cookie Policy</a>{' '}
          for a complete list.</p>
        </Section>

        <Section title="6. Data Retention">
          <p>Order records, documents, and audit logs are retained for as long as required to meet
          our contractual obligations to clients and applicable title-industry record-keeping
          requirements. Employee records are retained per PNA&apos;s HR retention schedule.
          Backups are rotated on a scheduled basis.</p>
        </Section>

        <Section title="7. Your Rights">
          <p>Depending on your jurisdiction, you may have rights to access, correct, or request
          deletion of your personal information. Employees and users may contact the
          administrator or our support team to exercise these rights. Requests concerning order
          data supplied by a client are coordinated with that client as the data controller.</p>
        </Section>

        <Section title="8. Changes to This Policy">
          <p>We may update this policy as the platform evolves. Material changes will be
          communicated within the application, and the &quot;Last updated&quot; date above will be
          revised.</p>
        </Section>

        <Section title="9. Contact">
          <p>Questions about this policy or our data practices:{' '}
            <a href="mailto:support@pnatitleservicesllc.com" className="text-blue-600 hover:underline">
              support@pnatitleservicesllc.com
            </a>
          </p>
        </Section>

        <div className="border-t border-slate-200 pt-6 flex items-center justify-between text-sm">
          <a href="/" className="text-blue-600 hover:text-blue-700 hover:underline">&larr; Back to TitleFlow</a>
          <a href="/cookie-policy" className="text-blue-600 hover:text-blue-700 hover:underline" data-testid="privacy-to-cookie-link">
            Cookie Policy &rarr;
          </a>
        </div>
      </main>
    </div>
  );
}
